import React, { useEffect, useRef } from 'react';
import { debounce, IconButton, OutlinedInput, Paper, Slide, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Fab from '@mui/material/Fab';
import NotesIcon from '@mui/icons-material/Notes';
import { StyleRules, WithStyles } from '@mui/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ScheduleIcon from '@mui/icons-material/Schedule';
import moment from 'moment';
import { updateLocationUWNote } from 'services/submission';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import { useSelector } from 'react-redux';
import { getSubmissionReadOnly, getTopLevelUserGroup } from 'application/redux/selectors';
import { getIsUnderwriterRole } from 'helpers/roles';
import { SubmissionDetails } from 'types/submission.type';
import { STATUS_TYPES } from '../../../../../constants';
import { withStyles } from '@mui/styles';
import Cancelable from '@mui/utils/debounce';
import { useEdisonDock } from '../../../../../hooks/EdisonChatDock';

interface LocationScratchPadProps {
    locationId: string;
    submission: SubmissionDetails;
    initialNotes?: string;
}

const styles = (): StyleRules => ({
    notesButton: {
        position: 'fixed',
        right: '3%',
        bottom: '5%',
    },
    notesPaper: { width: '100%', top: 'unset', bottom: '3%', height: '100%', borderRadius: '6px', zIndex: 1500 },
    notesPanel: { right: '3%', bottom: '5%' },
});

const LocationScratchPad: React.FunctionComponent<LocationScratchPadProps & WithStyles<typeof styles>> = (props) => {
    const { locationId, submission, classes, initialNotes } = props;
    const { updateSOVLocationUWNotes } = useActions(Actions);
    const group = useSelector(getTopLevelUserGroup);
    const isUnderwriter = getIsUnderwriterRole(group);
    const globalReadonly = useSelector(getSubmissionReadOnly);
    const { setOffsets, setHidden, isOpen: isEdisonOpen } = useEdisonDock();

    useEffect(() => {
        // e.g., move launcher 72px up while this page is active
        setOffsets({ bottom: 72 });
        return () => {
            setOffsets();
            setHidden(false);
        }; // reset on unmount
    }, []);

    const [notes, setNotes] = React.useState<string>(initialNotes || '');
    const [notesOpen, setNotesOpen] = React.useState<boolean>(false);
    const inputRef = useRef<any>();

    const updateHandler = useRef<any>();

    const handleAddDateTime = () => {
        const newNotes = notes + (notes ? '\n' : '') + moment().format('llll') + ' - ';
        setNotes(newNotes);
    };

    useEffect(() => setHidden(notesOpen), [notesOpen]);

    useEffect(() => {
        updateHandler.current && updateHandler.current(notes);
    }, [notes]);

    useEffect(() => {
        if (initialNotes !== notes) {
            setNotes(initialNotes || '');
        }
    }, [initialNotes]);

    useEffect(() => {
        updateHandler.current = debounce((newNotes: string) => {
            if (submission.id && locationId && newNotes !== initialNotes) {
                updateLocationUWNote(submission.id, locationId, newNotes);
                updateSOVLocationUWNotes(Number(locationId), newNotes);
            }
        }, 500);

        return () => {
            updateHandler.current && updateHandler.current.clear();
        };
    }, [submission.id, locationId]);

    const readonly =
        submission?.statusType.code === STATUS_TYPES.VOIDED || submission.isInForce || !isUnderwriter || globalReadonly;

    const panelWidth = 620;
    const panelHeight = 960;

    return (
        <>
            {!notesOpen && !isEdisonOpen && (
                <Fab
                    color={'secondary'}
                    className={classes.notesButton}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'secondary.main',
                            transform: 'scale(1.2)',
                        },
                    }}
                    onClick={() => {
                        setNotesOpen(true);
                        setTimeout(() => {
                            if (inputRef.current) {
                                inputRef.current.focus();
                                inputRef.current.setSelectionRange(
                                    inputRef.current.value.length,
                                    inputRef.current.value.length
                                );
                            }
                        }, 100);
                    }}
                >
                    <NotesIcon />
                </Fab>
            )}
            <Grid
                container
                role="dialog"
                aria-label="Agent Chat"
                className={classes.notesPanel}
                sx={{
                    position: 'fixed',
                    width: panelWidth,
                    height: panelHeight,
                    pointerEvents: notesOpen ? 'auto' : 'none',
                }}
            >
                <Slide direction="left" in={notesOpen}>
                    <Grid container direction={'column'}>
                        <Paper elevation={8} className={classes.notesPaper}>
                            {/*<Drawer*/}
                            {/*    PaperProps={{ className: classes.notesPaper }}*/}
                            {/*    variant={'persistent'}*/}
                            {/*    anchor={'right'}*/}
                            {/*    open={notesOpen}*/}
                            {/*    onClose={() => setNotesOpen(false)}*/}
                            {/*>*/}
                            <Grid container justifyContent={'space-between'}>
                                <Grid xs={2}>
                                    <IconButton onClick={() => setNotesOpen(false)} size="large">
                                        <ArrowForwardIosIcon fontSize={'large'} />
                                    </IconButton>
                                </Grid>
                                <Grid container justifyContent={'center'} direction={'column'} xs={9}>
                                    <Typography variant={'h2'}>Location Underwriting Notes</Typography>
                                </Grid>
                                <Grid
                                    xs={12}
                                    style={{
                                        paddingLeft: '20px',
                                        paddingRight: '20px',
                                        paddingTop: '10px',
                                        paddingBottom: '10px',
                                    }}
                                >
                                    <OutlinedInput
                                        readOnly={readonly}
                                        inputRef={inputRef}
                                        multiline
                                        rows={35}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </Grid>
                                {!readonly && (
                                    <Grid style={{ marginLeft: '10px' }} xs={1}>
                                        <IconButton onClick={handleAddDateTime} size="large">
                                            <ScheduleIcon />
                                        </IconButton>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                </Slide>
            </Grid>
        </>
    );
};

export default withStyles(styles)(LocationScratchPad);
